import { z } from 'zod'

export * from './eiffage-scans'

// Utils schemas
export type Resolution = z.infer<typeof resolutionSchema>
export const resolutionSchema = z.enum(['hour', 'day', 'week', 'month', 'quarter', 'year']).default('hour')

export const dateTimeSchema = z.string().datetime({ offset: true })

export type DateRangeSTR = z.infer<typeof dateRangeSchema>
export const dateRangeSchema = z.object({
  startDate: dateTimeSchema,
  endDate: dateTimeSchema
})

// Notification preferences
export type NotificationSubscription = z.infer<typeof notificationSubscriptionSchema>
export const notificationSubscriptionSchema = z.object({
  scope: z.enum(['site', 'group']),
  id: z.string(),
  topics: z.string().array().default(['*'])
})

export const orgMemberNotificationPreferencesSchema = z
  .object({
    mode: z.literal('all')
  })
  .or(
    z.object({
      mode: z.literal('custom'),
      subscriptions: z.array(notificationSubscriptionSchema).default([])
    })
  )

export type OrgMemberNotificationPreferences = z.infer<typeof orgMemberNotificationPreferencesSchema>

// Datasources
const commonDataSourcePropsSchema = z.object({
  id: z.string(),
  stationID: z.string(),
  createdAt: dateTimeSchema,
  notes: z.string().nullable(),
  readingMethod: z.enum(['incremental', 'cumulative']),
  unit: z.enum(['l', 'm3']),
  attachments: z.string().array().optional()
})

export type MeterDataSource = z.infer<typeof MeterDataSourceSchema>
export const MeterDataSourceSchema = commonDataSourcePropsSchema.extend({
  sourceType: z.literal('meter'),
  config: z.object({
    deviceID: z.string(),
    mappingQuery: z.string(),
    maker: z.string()
  })
})

export type ManualDataSource = z.infer<typeof ManualDataSourceSchema>
export const ManualDataSourceSchema = commonDataSourcePropsSchema.extend({
  sourceType: z.literal('manual'),
  config: z.object({})
})

export type DataSource = z.infer<typeof DataSourceSchema>
export const DataSourceSchema = z.discriminatedUnion('sourceType', [MeterDataSourceSchema, ManualDataSourceSchema])

export const LeakTriggerConfigSchema = z.object({
  enabled: z.boolean(),
  percentThreshold: z.number().min(0).max(1),
  volumeThreshold: z.number().min(0)
})

export type StationLeakTriggerConfigs = z.infer<typeof StationLeakTriggersConfigSchema>
export const StationLeakTriggersConfigSchema = z.object({
  'new-leak': LeakTriggerConfigSchema,
  'leak-update': LeakTriggerConfigSchema,
  'leak-resolved': LeakTriggerConfigSchema
})

export type GeoCoordinates = z.infer<typeof GeoCoordinatesSchema>
export const GeoCoordinatesSchema = z.object({
  lat: z.number(),
  lng: z.number()
})
